
import React, { useState, useEffect } from 'react';
import { SellAnyLogo } from './icons/SellAnyLogo';

interface HeaderProps {
  navigate: (path: string) => void;
  pathname: string;
}

const Header: React.FC<HeaderProps> = ({ navigate, pathname }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const leftNavLinks = [
    { path: '/', label: 'Home' },
    { path: '/residential', label: 'Residential' },
    { path: '/plots', label: 'Plots' },
    { path: '/commercial', label: 'Commercial' },
  ];

  const rightNavLinks = [
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
  ];

  const mobileNavLinks = [...leftNavLinks, ...rightNavLinks];

  const handleMobileNav = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 text-gray-800 border-b transition-all duration-300 ${isScrolled ? 'border-gray-200/75 shadow-sm bg-white/95 backdrop-blur-lg' : 'border-transparent bg-white/80 backdrop-blur-md'}`}>
        <div className="container mx-auto px-3 sm:px-4 lg:px-6">
          <div className="relative flex items-center justify-between h-16">
            {/* Left side: Burger menu on mobile, left nav links on desktop */}
            <div>
              {/* Mobile Menu Button */}
              <div className="lg:hidden">
                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-800 focus:outline-none p-2 -ml-2">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
                  </svg>
                </button>
              </div>
              {/* Desktop Navigation - Left */}
              <nav className="hidden lg:flex lg:items-center lg:space-x-8">
                {leftNavLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => navigate(link.path)}
                    className={`inline-block text-sm font-medium transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none ${pathname === link.path ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Center: Logo (absolutely positioned) */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <button onClick={() => navigate('/')} aria-label="SellAny Home">
                <SellAnyLogo className="h-10" />
              </button>
            </div>

            {/* Right: Right nav links & Action Button */}
            <div className="hidden lg:flex items-center justify-end">
              {/* Desktop Navigation - Right */}
              <nav className="flex items-center space-x-8">
                {rightNavLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => navigate(link.path)}
                    className={`inline-block text-sm font-medium transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none ${pathname === link.path ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
              <div className="ml-8">
                <button onClick={() => navigate('/list-property')} className="bg-brand-primary text-white text-sm font-medium py-2 px-5 rounded-full hover:bg-brand-primary/90 transition-all duration-300 transform hover:scale-105">
                  List Your Property
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 lg:hidden ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      ></div>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 left-0 bottom-0 w-full max-w-xs bg-white/95 backdrop-blur-xl z-50 transition-transform duration-300 ease-in-out lg:hidden ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="p-5">
          <div className="flex justify-between items-center mb-10">
            <SellAnyLogo className="h-10" />
            <button onClick={() => setIsMenuOpen(false)} className="text-gray-800 focus:outline-none p-2 -mr-2">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="flex flex-col space-y-2">
            {mobileNavLinks.map((link, index) => (
              <button
                key={link.label}
                onClick={() => handleMobileNav(link.path)}
                className={`w-full text-left block px-3 py-2.5 rounded-md text-base font-medium transition-all duration-300 ease-in-out ${pathname === link.path ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                  } ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${100 + index * 50}ms` }}
              >
                {link.label}
              </button>
            ))}
          </nav>
          <button onClick={() => handleMobileNav('/list-property')} className={`block w-full text-center mt-8 px-3 py-3 rounded-full text-base font-medium bg-brand-primary text-white hover:bg-brand-primary/90 transition-all duration-300 ease-in-out ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: `${100 + mobileNavLinks.length * 50}ms` }}
          >
            List Your Property
          </button>
        </div>
      </div>
    </>
  );
};

export default Header;
